import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Button,
  FormControl,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  Box,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SettingsIcon from '@mui/icons-material/Settings';
import LinearProgress from '@mui/material/LinearProgress';

import API from 'util/API';
import { showSuccessSnackbar, showErrorSnackbar } from 'store/actions/snackbarMessage';
import { useMutation } from '@tanstack/react-query';
import QrCodeSettingsDialog from './QrCodeSettingsDialog';
import QrCodePreview from './QrCodePreview';
import { generateBatchPDF as generateBatchPDFUtil } from './generateBatchPDF';
import QrCodeEditDialog from './QrCodeEditDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  card: {
    marginBottom: theme.spacing(3),
  },
  backButton: {
    marginBottom: theme.spacing(2),
  },
  simpleFormControl: {
    marginBottom: theme.spacing(2),
  },
}));

const ORDER_TYPES = [
  { value: 'dinein', label: 'Dine-in' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_OPTIONS = [
  { value: 'required', label: 'Required' },
  { value: 'not_required', label: 'Not Required' },
  { value: 'optional', label: 'Optional' },
];

const EXPIRE_TIME_OPTIONS = [
  { value: 0, label: 'Never' },
  { value: 1, label: '1 hour' },
  { value: 2, label: '2 hours' },
  { value: 4, label: '4 hours' },
  { value: 8, label: '8 hours' },
  { value: 24, label: '24 hours' },
  { value: 48, label: '48 hours' },
  { value: 168, label: '1 week' },
];

const POS_ACCEPT_OPTIONS = [
  { value: 'automatic', label: 'Automatically' },
  { value: 'manual', label: 'Manual' },
];

const createEnhancedShortUrl = async (params) => {
  const response = await API.post('/createEnhancedRestaurantOrderShortUrl', params);
  return response.data;
};

const QrCodeGenerator = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const hiddenQrRef = useRef(null);

  const restaurant = useSelector((state) => state.restaurant.selectedRestaurant);

  // Load settings from restaurant.qrCodeSettings
  const [qrCodeSettings, setQrCodeSettings] = useState([]);
  const [selectedOrderType, setSelectedOrderType] = useState('dinein');
  const [tableNumber, setTableNumber] = useState('');
  const [uiLoading, setUiLoading] = useState(false);
  const [generatedQrCode, setGeneratedQrCode] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [shortUrlCache, setShortUrlCache] = useState(new Map());

  // Dialog state for editing
  const [editOrderType, setEditOrderType] = useState('dinein');
  const [editPayment, setEditPayment] = useState('required');
  const [editExpireTime, setEditExpireTime] = useState(0);
  const [editPosAcceptOrders, setEditPosAcceptOrders] = useState('automatic');
  const [editDisabled, setEditDisabled] = useState(false);
  const [startTableNumber, setStartTableNumber] = useState('');
  const [endTableNumber, setEndTableNumber] = useState('');

  const showError = (msg) => dispatch(showErrorSnackbar(msg));
  const showSuccess = (msg) => dispatch(showSuccessSnackbar(msg));

  const createShortUrlMutation = useMutation({
    mutationFn: createEnhancedShortUrl,
    onError: (error) => {
      console.error('Short URL creation error:', error);
      showError(error.response?.data?.message || 'Failed to create short URL');
    }
  });

  // Load QR code settings from restaurant
  useEffect(() => {
    if (restaurant?.qrCodeSettings) {
      setQrCodeSettings(restaurant.qrCodeSettings);
    }
  }, [restaurant]);

  // Get current setting for selected order type
  const currentSetting = useMemo(() => {
    return qrCodeSettings.find(s => s.orderType === selectedOrderType) || {
      name: selectedOrderType,
      shortUrlCode: '',
      disabled: false,
      shortUrl: '',
      orderType: selectedOrderType,
      payment: 'required',
      posAcceptOrders: 'automatic'
    };
  }, [qrCodeSettings, selectedOrderType]);

  const generateQrCodeUrl = useCallback((tableNum = null, settings = null) => {
    if (!restaurant) return '';

    const settingsToUse = settings || currentSetting;
    const baseUrl = `https://${window.location.hostname}`;
    const restaurantId = restaurant.urlIdentifier;

    const params = new URLSearchParams();
    params.set('orderType', settingsToUse.orderType || selectedOrderType);
    params.set('payment', settingsToUse.payment || 'required');
    params.set('posAccept', settingsToUse.posAcceptOrders || 'automatic');

    const table = tableNum !== null ? tableNum : tableNumber.trim();
    if (table) {
      params.set('table', table);
    }

    const tableCode = `custom_${settingsToUse.orderType || selectedOrderType}_${Date.now()}`;

    return `${baseUrl}/online/${encodeURI(restaurantId)}/${tableCode}?${params.toString()}`;
  }, [restaurant, currentSetting, selectedOrderType, tableNumber]);

  const handleGetQrCode = async () => {
    if (!restaurant) {
      showError('Please select a restaurant first.');
      return;
    }

    setUiLoading(true);

    try {
      // Check if there's already a shortUrl for this setting
      let shortUrl = currentSetting.shortUrl;

      if (!shortUrl) {
        // Create short URL with enhanced parameters
        const shortUrlResult = await createShortUrlMutation.mutateAsync({
          orderType: currentSetting.orderType || selectedOrderType,
          payment: currentSetting.payment || 'required',
          posAcceptOrders: currentSetting.posAcceptOrders || 'automatic',
          tableNumber: tableNumber.trim() || null,
          expiresTimestamp: null,
        });

        shortUrl = shortUrlResult.shortUrl;
      }
      
      const qrCodeData = {
        orderType: currentSetting.orderType || selectedOrderType,
        payment: currentSetting.payment || 'required',
        posAcceptOrders: currentSetting.posAcceptOrders || 'automatic',
        tableNumber: tableNumber.trim(),
        createdAt: Date.now(),
        url: shortUrl || generateQrCodeUrl(),
        shortUrl: shortUrl,
      };

      setGeneratedQrCode(qrCodeData);
      showSuccess('QR Code generated successfully!');
    } catch (error) {
      console.error('Error generating QR code:', error);
      setGeneratedQrCode({
        orderType: currentSetting.orderType || selectedOrderType,
        payment: currentSetting.payment || 'required',
        posAcceptOrders: currentSetting.posAcceptOrders || 'automatic',
        tableNumber: tableNumber.trim(),
        createdAt: Date.now(),
        url: generateQrCodeUrl(),
      });
      showSuccess('QR Code generated with long URL (fallback)');
    } finally {
      setUiLoading(false);
    }
  };

  const handleOpenEditDialog = () => {
    // Initialize dialog state with current QR code settings
    setEditOrderType(generatedQrCode?.orderType || selectedOrderType);
    setEditPayment(generatedQrCode?.payment || currentSetting.payment || 'required');
    setEditPosAcceptOrders(generatedQrCode?.posAcceptOrders || currentSetting.posAcceptOrders || 'automatic');
    setEditDisabled(generatedQrCode?.disabled ?? currentSetting.disabled ?? false);
    setEditExpireTime(0);
    setEditDialogOpen(true);
  };

  const handleSaveEditedQrCode = async () => {
    setUiLoading(true);

    try {
      // Calculate expiry timestamp (milliseconds) - convert hours to timestamp
      const expiresTimestamp = editExpireTime > 0 ? Date.now() + (editExpireTime * 60 * 60 * 1000) : null;
      
      // Call the new updateQrCodeSettings API with timestamp
      await API.post('/updateQrCodeSettings', {
        tableNumber: tableNumber.trim() || null,
        paymentRequired: editPayment,
        expiryTime: expiresTimestamp,
        disabled: editDisabled,
      });
      
      // Create short URL with updated parameters
      const shortUrlResult = await createShortUrlMutation.mutateAsync({
        orderType: editOrderType,
        payment: editPayment,
        posAcceptOrders: editPosAcceptOrders,
        tableNumber: tableNumber.trim() || null,
        expiresTimestamp,
        disabled: editDisabled,
      });

      const shortUrl = shortUrlResult.shortUrl;
      
      const qrCodeData = {
        orderType: editOrderType,
        payment: editPayment,
        expireTime: editExpireTime,
        posAcceptOrders: editPosAcceptOrders,
        disabled: editDisabled,
        tableNumber: tableNumber.trim(),
        createdAt: Date.now(),
        expiresAt: expiresTimestamp,
        url: shortUrl || generateQrCodeUrl(null, { orderType: editOrderType, payment: editPayment, posAcceptOrders: editPosAcceptOrders }),
        shortUrl: shortUrl,
      };

      setGeneratedQrCode(qrCodeData);
      setEditDialogOpen(false);
      showSuccess('QR Code updated successfully!');
    } catch (error) {
      console.error('Error updating QR code:', error);
      showError('Failed to update QR code');
    } finally {
      setUiLoading(false);
    }
  };

  const downloadQRCode = () => {
    const canvas = document.getElementById('generated-qr-code');
    if (!canvas) return;

    const pngUrl = canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream');
    const downloadLink = document.createElement('a');
    downloadLink.href = pngUrl;
    downloadLink.download = `${restaurant?.urlIdentifier}-${selectedOrderType}-qrcode.png`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const copyToClipboard = () => {
    const urlToCopy = (generatedQrCode && generatedQrCode.shortUrl) ? generatedQrCode.shortUrl : generateQrCodeUrl();
    navigator.clipboard.writeText(urlToCopy);
    showSuccess('URL copied to clipboard!');
  };

  const handleBack = () => {
    history.push('/myrestaurant');
  };

  const handleOpenSettings = () => {
    setSettingsDialogOpen(true);
  };

  const handleSettingsUpdate = (updatedSettings) => {
    setQrCodeSettings(updatedSettings);
  };

  const generateBatchPDF = async () => {
    const start = parseInt(startTableNumber, 10);
    const end = parseInt(endTableNumber, 10);

    if (isNaN(start) || isNaN(end)) {
      showError('Please enter valid start and end table numbers.');
      return;
    }

    if (start > end) {
      showError('Start table number must be less than or equal to end table number.');
      return;
    }

    if (end - start > 100) {
      showError('Maximum 100 tables can be generated at once.');
      return;
    }

    setUiLoading(true);
    setPdfProgress(0);

    try {
      const totalTables = end - start + 1;
      
      const updatedCache = await generateBatchPDFUtil({
        startTableNumber: start,
        endTableNumber: end,
        editOrderType,
        editPayment,
        editPosAcceptOrders,
        generateQrCodeUrl,
        createEnhancedShortUrl,
        shortUrlCache,
        setPdfProgress,
        restaurant,
      });

      // Update the cache state
      setShortUrlCache(updatedCache);
      showSuccess(`PDF generated with ${totalTables} table QR codes!`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Error generating PDF. Please try again.');
    } finally {
      setUiLoading(false);
      setPdfProgress(0);
    }
  };

  if (!restaurant) {
    return (
      <div className={classes.root}>
        <Typography color="error">
          Please go back to the restaurant screen.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      {uiLoading && <LinearProgress variant={pdfProgress > 0 ? "determinate" : "indeterminate"} value={pdfProgress} />}

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        className={classes.backButton}
      >
        Back to My Restaurant
      </Button>

      <Grid container spacing={3}>
        {/* Simple QR Code Generator */}
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <CardHeader title="Generate QR Code" />
            <CardContent>
              <FormControl fullWidth className={classes.simpleFormControl}>
                <InputLabel id="order-type-label">Order Type</InputLabel>
                <Select
                  labelId="order-type-label"
                  value={selectedOrderType}
                  label="Order Type"
                  onChange={(e) => setSelectedOrderType(e.target.value)}
                >
                  {ORDER_TYPES.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Table Number (Optional)"
                variant="outlined"
                value={tableNumber}
                onChange={(e) => setTableNumber(e.target.value)}
                helperText="Leave empty for general QR code"
                className={classes.simpleFormControl}
              />

              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGetQrCode}
                disabled={uiLoading}
              >
                Get QR Code
              </Button>

              {/* QR Code Settings Display */}
              <Box mt={3}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">
                    QR Code Settings
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<SettingsIcon />}
                    onClick={handleOpenSettings}
                  >
                    View All Settings
                  </Button>
                </Box>
                
                <Box mt={2}>
                  <Typography variant="body2" color="textSecondary">
                    <strong>{ORDER_TYPES.find(o => o.value === selectedOrderType)?.label}:</strong>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Payment: {PAYMENT_OPTIONS.find(o => o.value === currentSetting.payment)?.label || 'Required'}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    POS Accept: {POS_ACCEPT_OPTIONS.find(o => o.value === currentSetting.posAcceptOrders)?.label || 'Automatically'}
                  </Typography>
                  {currentSetting.disabled && (
                    <Typography variant="body2" color="error">
                      This QR code type is currently disabled
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* QR Code Display */}
        <Grid item xs={12} md={6}>
          <QrCodePreview
            generatedQrCode={generatedQrCode}
            restaurant={restaurant}
            onDownload={downloadQRCode}
            onCopyToClipboard={copyToClipboard}
            onEdit={handleOpenEditDialog}
          />
        </Grid>
      </Grid>

      {/* QR Code Settings Dialog */}
      <QrCodeSettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        qrCodeSettings={qrCodeSettings}
        onSettingsUpdate={handleSettingsUpdate}
        showSuccess={showSuccess}
        showError={showError}
        uiLoading={uiLoading}
        setUiLoading={setUiLoading}
      />

      {/* Edit QR Code Dialog */}
      <QrCodeEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        editOrderType={editOrderType}
        setEditOrderType={setEditOrderType}
        editPayment={editPayment}
        setEditPayment={setEditPayment}
        editExpireTime={editExpireTime}
        setEditExpireTime={setEditExpireTime}
        editPosAcceptOrders={editPosAcceptOrders}
        setEditPosAcceptOrders={setEditPosAcceptOrders}
        editDisabled={editDisabled}
        setEditDisabled={setEditDisabled}
        startTableNumber={startTableNumber}
        setStartTableNumber={setStartTableNumber}
        endTableNumber={endTableNumber}
        setEndTableNumber={setEndTableNumber}
        uiLoading={uiLoading}
        pdfProgress={pdfProgress}
        onSave={handleSaveEditedQrCode}
        onGenerateBatchPDF={generateBatchPDF}
      />

      {/* Hidden container for batch QR code generation */}
      <div ref={hiddenQrRef} style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }} />
    </div>
  );
};

export default QrCodeGenerator;
